'use client'

import { useMemo, type ReactElement } from 'react'
import QRCode from 'qrcode'
import { APP_CONFIG } from '../lib/config'

interface AppQRCodeProps {
  /** Pixel size of the (square) QR. */
  size?: number
  className?: string
}

// Each dark module is drawn as a separate square smaller than its cell, leaving
// a gap — that's the dotted look. Kept modest (and error-correction at 'Q') so
// the code stays reliably scannable.
const MODULE_SCALE = 0.82
const QUIET_ZONE = 2 // white border, in modules

/**
 * Real, scannable QR encoding the app smart-link (APP_CONFIG.downloads.smartLink,
 * env-overridable via NEXT_PUBLIC_APP_LINK), rendered as separated squares to
 * match the original dotted look. No center logo.
 */
export function AppQRCode({ size = 188, className }: AppQRCodeProps) {
  const matrix = useMemo(() => {
    const qr = QRCode.create(APP_CONFIG.downloads.smartLink, { errorCorrectionLevel: 'Q' })
    return { data: qr.modules.data, count: qr.modules.size }
  }, [])

  const total = matrix.count + QUIET_ZONE * 2
  const pitch = size / total
  const dot = pitch * MODULE_SCALE
  const offset = (pitch - dot) / 2

  const dots: ReactElement[] = []
  for (let row = 0; row < matrix.count; row++) {
    for (let col = 0; col < matrix.count; col++) {
      if (matrix.data[row * matrix.count + col]) {
        dots.push(
          <rect
            key={`${row}-${col}`}
            x={(col + QUIET_ZONE) * pitch + offset}
            y={(row + QUIET_ZONE) * pitch + offset}
            width={dot}
            height={dot}
            fill="#091410"
          />
        )
      }
    }
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      shapeRendering="crispEdges"
      role="img"
      aria-label="QR code to download Finley"
    >
      <rect width={size} height={size} fill="#ffffff" />
      {dots}
    </svg>
  )
}
